// The backend answers every error as RFC 9457 problem details (backend shared/api/problems.py).
import { t } from "@/shared/i18n/i18n";

export interface FieldError { field: string; message: string }

export interface Problem {
  title: string;
  status: number;
  code: string;
  detail?: string;
  errors?: FieldError[];
}

export function isProblem(x: unknown): x is Problem {
  return !!x && typeof x === "object" && typeof (x as Problem).code === "string" && typeof (x as Problem).status === "number";
}

export class ApiError extends Error {
  readonly problem: Problem;

  constructor(problem: Problem) {
    super(problem.title);
    this.name = "ApiError";
    this.problem = problem;
  }

  get code(): string { return this.problem.code; }
  get status(): number { return this.problem.status; }

  /** Field errors keyed by the form's field names (the API speaks camelCase, like the forms). */
  fields(): Record<string, string> {
    return Object.fromEntries((this.problem.errors ?? []).map((e) => [e.field, e.message]));
  }
}

export function toApiError(error: unknown, status: number): ApiError {
  if (isProblem(error)) return new ApiError(error);
  return new ApiError({ title: status ? t("common:errors.status", { status }) : t("common:errors.unreachable"), status, code: "unexpected" });
}

export function messageOf(error: unknown): string {
  if (error instanceof ApiError) return error.problem.title;
  if (error instanceof Error) return error.message;
  return t("common:errors.generic");
}
